import { Button, Field, Input } from '@ambernord/ui';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader.js';
import { requestPasswordReset } from '../lib/api.js';
import { playAudioCue } from '../lib/audio.js';

/** Aizmirsta parole: e-pasts → serveris vienmēr atbild 200 (neizpauž konta esamību). */
export default function ForgotPassword() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await requestPasswordReset(email.trim());
      if (!res.ok) {
        setError(res.status === 429 ? t('forgotPassword.rateLimited') : t('status.error'));
        playAudioCue('error');
        return;
      }
      playAudioCue('success');
      setDone(true);
    } catch {
      setError(t('status.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col">
      <AppHeader />
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-4 py-10">
        <section className="rounded-lg border border-slate-200 p-6 dark:border-slate-800">
          <h2 className="mb-1 text-base font-medium">{t('forgotPassword.heading')}</h2>
          {done ? (
            <div data-testid="forgot-password-done">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                {t('forgotPassword.done')}
              </p>
              <p className="mt-4 text-sm">
                <Link className="text-brand-600 hover:underline dark:text-brand-400" to="/login">
                  {t('setPassword.goToSignIn')}
                </Link>
              </p>
            </div>
          ) : (
            <>
              <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
                {t('forgotPassword.description')}
              </p>
              <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
                <Field label={t('login.email')} htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Field>
                {error && (
                  <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                    {error}
                  </p>
                )}
                <Button type="submit" disabled={submitting || !email.trim()}>
                  {submitting ? t('status.loading') : t('forgotPassword.submit')}
                </Button>
              </form>
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
                <Link className="text-brand-600 hover:underline dark:text-brand-400" to="/login">
                  {t('register.backToSignIn')}
                </Link>
              </p>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
